import { useState, useEffect } from 'react';
import { isIOS, isAndroid } from '../utils/pwaUtils';
import { detectBrowser } from '../utils/deviceDetection';
import { getDeferredPrompt, setDeferredPrompt as setGlobalDeferredPrompt } from '../utils/pwaEvent';
import './PWAInstallPage.css';

export default function PWAInstallPage({ onInstall, onSkip }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // 1: install button, 2: instructions
  const [platform, setPlatform] = useState('unknown');
  const [browser, setBrowser] = useState('unknown');
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Определяем платформу и браузер
    const platformType = isIOS() ? 'ios' : isAndroid() ? 'android' : 'unknown';
    setPlatform(platformType);
    setBrowser(detectBrowser());

    // Проверяем, было ли событие уже перехвачено глобально
    const globalPrompt = getDeferredPrompt();
    if (globalPrompt) {
      console.log('Found global deferred prompt');
      setDeferredPrompt(globalPrompt);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      console.log('Captured beforeinstallprompt event in component');
      setDeferredPrompt(e);
      setGlobalDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setGlobalDeferredPrompt(null);
      setIsInstalling(false);
      onInstall();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [onInstall]);

  // Обработчик нажатия кнопки "Установить"
  const handleInstallClick = async () => {
    if (isAndroid()) {
      // Android logic
      if (deferredPrompt) {
        // Если браузер поддерживает установку, запускаем скрипт
        setIsInstalling(true);
        try {
          deferredPrompt.prompt();
          const { outcome } = await deferredPrompt.userChoice;

          if (outcome === 'accepted') {
            setDeferredPrompt(null);
          }
        } catch (error) {
          console.error('Installation error:', error);
        } finally {
          setIsInstalling(false);
        }
      } else {
        // Если не поддерживает (или уже установлено/недоступно), показываем инструкцию
        setCurrentStep(2);
      }
    } else if (isIOS()) {
      // iOS logic - всегда показываем инструкции
      setCurrentStep(2);
    } else {
      // Fallback for other platforms (e.g. Desktop)
      if (deferredPrompt) {
        deferredPrompt.prompt();
      } else {
        // Если нет prompt, возможно это десктоп без поддержки или уже установлено
        // Можно просто пропустить или показать сообщение
        onSkip();
      }
    }
  };

  // Рендер инструкций для iOS
  const renderIOSInstructions = () => {
    // Вариант 1: Instagram WebView
    if (browser === 'Instagram WebView') {
      return (
        <>
          <p className="pwa-install-step">Шаг 1/2</p>
          <h1 className="pwa-install-title">Перейдите в браузер</h1>
          <p className="pwa-install-description">
            Нажмите <span className="more-icon">
              <img
                src="/icons/share-android.svg"
                alt="Share icon"
                style={{ transform: 'rotate(90deg)', width: '16px', height: '16px' }}
              />
            </span> сверху и&nbsp;выберите <br />«Открыть&nbsp;в&nbsp;стороннем&nbsp;браузере»
          </p>
          <div className="pwa-install-icon">
            {/* Используем существующую картинку или плейсхолдер, если специфичной нет */}
            <img
              src="/app-install-1-inst.png"
              alt="Instagram Installation Instructions"
              className="pwa-install-image"
            />
          </div>
        </>
      );
    }

    // Вариант 2: Telegram WebView
    if (browser === 'Telegram WebView') {
      return (
        <>
          <p className="pwa-install-step">Шаг 1/2</p>
          <h1 className="pwa-install-title">Перейдите в браузер</h1>
          <p className="pwa-install-description">
            Нажмите <span className="safari-icon"><img src="/icons/safari.svg" alt="Safari icon" /></span> в&nbsp;нижнем углу
            <br />и&nbsp;откройте Safari
          </p>
          <div className="pwa-install-icon">
            <img
              src="/app-install-1-tg.svg"
              alt="Telegram Installation Instructions"
              className="pwa-install-image"
            />
          </div>
        </>
      );
    }

    // Вариант 3: Остальные (Safari и др.)
    return (
      <>
        <p className="pwa-install-step">Шаг 2/2</p>
        <h1 className="pwa-install-title">Установите приложение</h1>
        <p className="pwa-install-description">
          Нажмите <span className="share-icon"><img src="/icons/share-PWA-icon.svg" alt="Поделиться" /></span> в&nbsp;меню браузера, пролистайте
          <br />чуть ниже и&nbsp;выберите «На&nbsp;экран Домой»
        </p>
        <div className="pwa-install-icon">
          <img
            src="/app-install-2-ios.png"
            alt="iOS Installation Instructions"
            className="pwa-install-image"
          />
        </div>
      </>
    );
  };

  // Рендер инструкций для Android
  const renderAndroidInstructions = () => {
    return (
      <>
        <p className="pwa-install-step">Шаг 1/2</p>
        <h1 className="pwa-install-title">Перейдите в браузер</h1>
        <p className="pwa-install-description">
          Нажмите <span className="more-icon">
            <img
              src="/icons/share-android.svg"
              alt="Share icon"
              style={{ transform: 'rotate(90deg)', width: '16px', height: '16px' }}
            />
          </span> сверху и&nbsp;выберите пункт <br />«Открыть&nbsp;в&nbsp;стороннем&nbsp;браузере»
        </p>
        <div className="pwa-install-icon">
          <img
            src="/app-install-2-android.png"
            alt="Android Installation Instructions"
            className="pwa-install-image"
          />
        </div>
      </>
    );
  };

  // Step 1: Кнопка установки
  if (currentStep === 1) {
    return (
      <div className="pwa-install-page">
        <div className="pwa-install-content">
          <div className="pwa-install-icon">
            <img
              src="/app-install.svg"
              alt="Install App"
              className="pwa-install-image"
            />
          </div>

          <h1 className="pwa-install-title">Установите приложение</h1>

          <p className="pwa-install-description">
            Добавьте иконку Sense на&nbsp;главный экран для&nbsp;удобного использования
          </p>

          <button
            className={`pwa-install-button ${isInstalling ? 'installing' : ''}`}
            onClick={handleInstallClick}
            disabled={isInstalling}
          >
            <span className="install-icon">
              <svg viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 12.5H12M12 12.5H16M12 12.5V16.5M12 12.5V8.5M4 17.3002V7.7002C4 6.58009 4 6.01962 4.21799 5.5918C4.40973 5.21547 4.71547 4.90973 5.0918 4.71799C5.51962 4.5 6.08009 4.5 7.2002 4.5H16.8002C17.9203 4.5 18.4801 4.5 18.9079 4.71799C19.2842 4.90973 19.5905 5.21547 19.7822 5.5918C20.0002 6.01962 20.0002 6.57967 20.0002 7.69978V17.2998C20.0002 18.4199 20.0002 18.98 19.7822 19.4078C19.5905 19.7841 19.2842 20.0905 18.9079 20.2822C18.4805 20.5 17.9215 20.5 16.8036 20.5H7.19691C6.07899 20.5 5.5192 20.5 5.0918 20.2822C4.71547 20.0905 4.40973 19.7842 4.21799 19.4079C4 18.9801 4 18.4203 4 17.3002Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
            {isInstalling ? 'Установка...' : 'Установить'}
          </button>

          <button className="pwa-install-skip" onClick={onSkip}>
            Пропустить установку
          </button>
        </div>

        <button className="pwa-help-button" aria-label="Помощь" onClick={() => window.open('https://t.me/SenseApp_form', '_blank')}>
          <img src="/icons/help-profile.svg" alt="" aria-hidden="true" />
        </button>
      </div>
    );
  }

  // Step 2: Инструкции
  if (currentStep === 2) {
    return (
      <div className="pwa-install-page">
        <div className="pwa-install-content">
          {platform === 'ios' ? renderIOSInstructions() : renderAndroidInstructions()}

          <button className="pwa-install-skip" onClick={onSkip}>
            Пропустить установку
          </button>
        </div>

        <button className="pwa-help-button" aria-label="Помощь" onClick={() => window.open('https://t.me/SenseApp_form', '_blank')}>
          <img src="/icons/help-profile.svg" alt="" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return null;
}
